import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { logError } from '@/api/errorLog';

/**
 * Mini ErrorBoundary maison (class component React) pour le module Game Autopsy.
 *
 * Pourquoi pas `react-error-boundary` ? YAGNI (CONVENTIONS §1). Le composant
 * ne sert qu'à un endroit (Autopsy), le contrat est ~30 lignes, ajouter une
 * dep pour ça serait disproportionné.
 *
 * MVP2.5 T8 (bug A4 — « Lancer l'autopsie » ne trigger rien visiblement sur
 * tablette Safari iPad). Quand le runner Stockfish ou n'importe quel enfant
 * jette une erreur runtime React, on affiche un fallback coral kid-friendly
 * + on logge backend via `errorLog.logError()` (rate-limit 30/min/IP +
 * cap 50/session).
 */

interface Props {
  children: ReactNode;
  /** Optionnel : module logique pour le payload de log (defaults to 'autopsy'). */
  module?: string;
}

interface State {
  error: Error | null;
}

export class AutopsyErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const context: { module: string; extra: Record<string, unknown> } = {
      module: this.props.module ?? 'autopsy',
      extra: { componentStack: info.componentStack ?? '(no stack)' },
    };
    void logError({
      message: `[autopsy:error-boundary] ${error.message}`,
      error,
      context,
    });
  }

  handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) {
      return this.props.children;
    }
    const detail = error.message.length > 140 ? `${error.message.slice(0, 140)}…` : error.message;
    return (
      <div
        role="alert"
        className="relative p-4 rounded-lg glass glass-coral"
        data-testid="autopsy-error-boundary"
      >
        <span
          aria-hidden="true"
          className="absolute top-3 right-3 inline-flex items-center justify-center w-6 h-6 rounded border font-mono text-xs"
          style={{
            color: 'var(--coral-500)',
            borderColor: 'var(--coral-500)33',
            backgroundColor: 'var(--coral-500)10',
          }}
        >
          !
        </span>
        <div className="mr-9">
          <span
            className="block text-caption uppercase mb-2"
            style={{ color: 'var(--coral-500)', letterSpacing: '0.05em', fontWeight: 500 }}
          >
            Erreur autopsie
          </span>
          <p className="text-body-sm text-paper-50 leading-relaxed">
            Une erreur a interrompu l'autopsie.{' '}
            <span className="font-mono text-xs text-coral-500">{detail}</span>. Réessaie ou
            recharge la page.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={this.handleReset}
              className="px-3 py-1.5 rounded border border-coral-500/40 bg-coral-500/10 text-coral-500 font-mono text-xs hover:bg-coral-500/20 transition-colors duration-150"
            >
              Réessayer
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 rounded border border-ink-700 bg-paper-50/[0.02] text-ink-300 font-mono text-xs hover:text-paper-50 transition-colors duration-150"
            >
              Recharger la page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
